import { useEffect, useState } from 'react';
import { useRouteLoaderData, Link, Outlet, useNavigate } from 'react-router';
import GettingStartedPrompt from '@/components/core/GettingStartedPrompt';
import MembersList from '@/components/members/MembersList';
import useIsAdmin from '@/hooks/useIsAdmin';
import PathwayRow from '@/components/pathways/PathwayRow';
import SystemRow from '@/components/systems/SystemRow';
import { resourcesActions, useNextPathways, usePathways, useSystems } from '@/stores/resourcesStore';
import LoadMoreButton from '@/components/core/LoadMoreButton';
import featureConfig from '@/featureConfig';
import BulkActionsToolbar from '@/components/projects/BulkActionsToolbar';
import BulkDuplicateModal from '@/components/projects/BulkDuplicateModal';
import {
  bulkAnalyzePathways,
  bulkBalancePathways,
  bulkDeletePathways,
  bulkDuplicatePathways,
  getPathways,
} from '@/api/pathways';
import { bulkDeleteSystems, bulkDuplicateSystems, getSystems } from '@/api/systems';
import { confirm } from '@/components/core/createConfirmation';
import { toast } from '@/utils/toast';
import { getProjects } from '@/api/projects';

const EMPTY_ARRAY = [];

const ProjectHomeRoute = () => {
  const {
    project: { id, members },
  } = useRouteLoaderData('project');

  const nextPathways = useNextPathways();
  const pathways = usePathways() ?? EMPTY_ARRAY;
  const systems = useSystems() ?? EMPTY_ARRAY;
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [selectedPathwayIds, setSelectedPathwayIds] = useState([]);
  const [selectedSystemIds, setSelectedSystemIds] = useState([]);
  const [pendingAction, setPendingAction] = useState(null);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsNextPage, setProjectsNextPage] = useState(null);
  const [duplicateModal, setDuplicateModal] = useState(null);

  useEffect(() => {
    const pathwayIds = new Set(pathways.map(({ id }) => id));
    setSelectedPathwayIds(selected => selected.filter(pathwayId => pathwayIds.has(pathwayId)));
  }, [pathways]);

  useEffect(() => {
    const systemIds = new Set(systems.map(({ id }) => id));
    setSelectedSystemIds(selected => selected.filter(systemId => systemIds.has(systemId)));
  }, [systems]);

  useEffect(() => {
    if (!duplicateModal) return;

    let active = true;

    const loadProjects = async () => {
      setIsProjectsLoading(true);

      try {
        const {
          data: { next, results },
        } = await getProjects();

        if (active) {
          setProjects(results.map(project => ({ label: project.name, value: project.id })));
          setProjectsNextPage(next);
        }
      } catch {
        toast.error('Unable to load projects.');
        if (active) setDuplicateModal(null);
      } finally {
        if (active) {
          setIsProjectsLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      active = false;
    };
  }, [duplicateModal]);

  const loadMore = () => {
    resourcesActions.loadMorePathways(id);
  };

  const toggleSelection = (setter, resourceId, checked) => {
    setter(selected =>
      checked
        ? Array.from(new Set([...selected, resourceId]))
        : selected.filter(selectedId => selectedId !== resourceId),
    );
  };

  const toggleAll = (setter, resources, selectedIds) => {
    setter(selectedIds.length === resources.length ? [] : resources.map(({ id }) => id));
  };

  const runBulkAction = async (actionKey, action, successMessage) => {
    setPendingAction(actionKey);

    try {
      await action();
      toast.success(successMessage);
    } catch (error) {
      toast.error(error?.response?.data?.detail ?? 'Bulk action failed.');
    } finally {
      setPendingAction(null);
    }
  };

  const openDuplicateModal = type => {
    const count = type === 'pathway' ? selectedPathwayIds.length : selectedSystemIds.length;
    if (count === 0) return;

    setDuplicateModal({ type, count });
  };

  const closeDuplicateModal = () => {
    setDuplicateModal(null);
  };

  const refreshPathways = async projectId => {
    const response = await getPathways(projectId);
    resourcesActions.setPathways(response.data);
  };

  const refreshSystems = async projectId => {
    const response = await getSystems(projectId);
    resourcesActions.setSystems(response.data);
  };

  const handleBulkPathwayAction = async (action, request) => {
    if (selectedPathwayIds.length === 0 || pendingAction) return;

    const pathwayIds = [...selectedPathwayIds];
    setPendingAction(`pathways-${action}`);

    try {
      const { data: results } = await request(id, { pathway_ids: pathwayIds });
      const startedIds = results.filter(result => !result.error).map(result => result.pathway_id);
      const failedCount = results.length - startedIds.length;
      const activity = action === 'balance' ? 'Balancing' : 'Analysis';

      startedIds.forEach(pathwayId => resourcesActions.updatePathway({ id: pathwayId, analysis_stale: true }));

      if (failedCount > 0) {
        const failureMessage = `${failedCount} pathway${failedCount === 1 ? '' : 's'} could not be ${action === 'balance' ? 'balanced' : 'analyzed'}. Check ${failedCount === 1 ? 'it' : 'them'} individually.`;
        toast.warning(
          startedIds.length > 0
            ? `${activity} started for ${startedIds.length} of ${results.length} pathways. ${failureMessage}`
            : failureMessage,
        );
      } else {
        toast.success(`${activity} started for ${startedIds.length} pathway${startedIds.length === 1 ? '' : 's'}.`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail ?? `Unable to ${action} selected pathways.`);
    } finally {
      setPendingAction(null);
    }
  };

  const handleDeletePathways = async () => {
    if (selectedPathwayIds.length === 0) return;

    const selectedPathways = pathways.filter(({ id: pathwayId }) => selectedPathwayIds.includes(pathwayId));
    if (selectedPathways.some(({ locked }) => locked)) {
      toast.error('Locked pathways cannot be deleted in bulk.');
      return;
    }

    const approved = await confirm(
      `Are you sure you want to remove ${selectedPathwayIds.length} pathway${selectedPathwayIds.length === 1 ? '' : 's'}?`,
    );
    if (!approved) return;

    return runBulkAction(
      'pathways-delete',
      async () => {
        const response = await bulkDeletePathways(id, { pathway_ids: selectedPathwayIds });
        if (response.status === 204) {
          resourcesActions.removePathways(selectedPathwayIds);
        }
        setSelectedPathwayIds([]);
      },
      `${selectedPathwayIds.length} pathway${selectedPathwayIds.length === 1 ? '' : 's'} deleted.`,
    );
  };

  const handleDeleteSystems = async () => {
    if (selectedSystemIds.length === 0) return;

    const approved = await confirm(
      `Are you sure you want to remove ${selectedSystemIds.length} system${selectedSystemIds.length === 1 ? '' : 's'}?`,
    );
    if (!approved) return;

    return runBulkAction(
      'systems-delete',
      async () => {
        const response = await bulkDeleteSystems(id, { system_ids: selectedSystemIds });
        if (response.status === 204) {
          resourcesActions.removeSystems(selectedSystemIds);
        }
        setSelectedSystemIds([]);
      },
      `${selectedSystemIds.length} system${selectedSystemIds.length === 1 ? '' : 's'} deleted.`,
    );
  };

  const handleBulkDuplicateSubmit = async event => {
    event.preventDefault();
    if (!duplicateModal) return;

    const formData = new FormData(event.currentTarget);
    const projectId = formData.get('project_id');
    const targetPath = `/projects/${projectId}/dashboard`;

    if (!projectId) return;

    if (duplicateModal.type === 'pathway') {
      await runBulkAction(
        'pathways-duplicate',
        async () => {
          await bulkDuplicatePathways(id, { pathway_ids: selectedPathwayIds, project_id: projectId });
          setSelectedPathwayIds([]);
          closeDuplicateModal();
          if (projectId === id) {
            await refreshPathways(id);
          } else {
            navigate(targetPath);
          }
        },
        `${selectedPathwayIds.length} pathway${selectedPathwayIds.length === 1 ? '' : 's'} copied.`,
      );
      return;
    }

    await runBulkAction(
      'systems-duplicate',
      async () => {
        await bulkDuplicateSystems(id, { system_ids: selectedSystemIds, project_id: projectId });
        setSelectedSystemIds([]);
        closeDuplicateModal();
        if (projectId === id) {
          await refreshSystems(id);
        } else {
          navigate(targetPath);
        }
      },
      `${selectedSystemIds.length} system${selectedSystemIds.length === 1 ? '' : 's'} copied.`,
    );
  };

  return (
    <div className="overflow-y-scroll">
      {featureConfig.enableWalkthroughs && <GettingStartedPrompt />}
      <div className="flex">
        <div className={`p-4 ${!featureConfig.managePathways ? 'w-full' : 'w-2/3'}`}>
          <div className="flex items-center justify-between pb-2 border-b mr-6 border-gray-200">
            <p className="text-lg font-semibold"> Pathways ({pathways.length})</p>
            {featureConfig.enablePathwayAdding && (
              <Link to={`pathways/new`} className="btn btn-secondary btn-xs">
                Add Pathway
              </Link>
            )}
          </div>

          <div className="mt-4">
            {selectedPathwayIds.length > 0 && (
              <BulkActionsToolbar
                count={selectedPathwayIds.length}
                itemLabel="pathway"
                onAnalyze={() => handleBulkPathwayAction('analyze', bulkAnalyzePathways)}
                onBalance={() => handleBulkPathwayAction('balance', bulkBalancePathways)}
                onCopy={() => openDuplicateModal('pathway')}
                onDelete={isAdmin ? handleDeletePathways : undefined}
                pending={pendingAction?.startsWith('pathways-')}
              />
            )}
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="w-10 py-2 pl-4 pr-0">
                    {pathways.length > 0 && (
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={selectedPathwayIds.length > 0 && selectedPathwayIds.length === pathways.length}
                        onChange={() => toggleAll(setSelectedPathwayIds, pathways, selectedPathwayIds)}
                        aria-label="Select all pathways"
                      />
                    )}
                  </th>
                  <th className="text-left w-1/4 py-2 px-4">Name</th>
                  <th className="text-left w-1/4 py-2 px-4">&nbsp;Status</th>
                  <th className="text-left w-1/4 py-2 px-4">Created</th>
                  <th className="text-left w-1/4 py-2 px-4">Last Updated</th>
                  <th className="text-right py-2 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {pathways.map((pathway, index) => (
                  <PathwayRow
                    key={index}
                    pathway={pathway}
                    projectId={id}
                    isAdmin={isAdmin}
                    isSelected={selectedPathwayIds.includes(pathway.id)}
                    onSelectionChange={(pathwayId, checked) =>
                      toggleSelection(setSelectedPathwayIds, pathwayId, checked)
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>

          <LoadMoreButton
            onLoadMore={loadMore}
            hasMore={!!nextPathways}
            className="btn btn-sm btn-outline"
            buttonText="Load more"
          />

          {featureConfig.manageSystems && (
            <>
              <div className="flex items-center justify-between pb-2 border-b mr-6 border-gray-200 mt-6">
                <p className="text-lg font-semibold">Systems ({systems.length})</p>
                <Link to="systems/new" className="btn btn-secondary btn-xs">
                  Add System
                </Link>
              </div>
              <div className="mt-4">
                {selectedSystemIds.length > 0 && (
                  <BulkActionsToolbar
                    count={selectedSystemIds.length}
                    itemLabel="system"
                    onCopy={() => openDuplicateModal('system')}
                    onDelete={isAdmin ? handleDeleteSystems : undefined}
                    pending={pendingAction?.startsWith('systems-')}
                  />
                )}
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <th className="w-10 py-2 pl-4 pr-0">
                        {systems.length > 0 && (
                          <input
                            type="checkbox"
                            className="checkbox checkbox-sm"
                            checked={selectedSystemIds.length > 0 && selectedSystemIds.length === systems.length}
                            onChange={() => toggleAll(setSelectedSystemIds, systems, selectedSystemIds)}
                            aria-label="Select all systems"
                          />
                        )}
                      </th>
                      <th className="text-left w-1/4 py-2 px-4">Name</th>
                      <th className="text-left w-1/4 py-2 px-4">&nbsp;Status</th>
                      <th className="text-left w-1/4 py-2 px-4">Created</th>
                      <th className="text-left w-1/4 py-2 px-4">Last Updated</th>
                      <th className="text-right py-2 px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {systems.map((system, index) => (
                      <SystemRow
                        key={index}
                        system={system}
                        projectId={id}
                        isSelected={selectedSystemIds.includes(system.id)}
                        onSelectionChange={(systemId, checked) =>
                          toggleSelection(setSelectedSystemIds, systemId, checked)
                        }
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        {featureConfig.manageMembers && (
          <div className="w-1/3 p-4">
            <div className="flex items-center justify-between pb-2 mr-6 border-b border-gray-200">
              <div className="flex items-center">
                <p className="pr-2 text-lg font-semibold">Members ({members.length})</p>
              </div>
              <Link to="new-member" className="btn btn-secondary btn-xs">
                Add Member
              </Link>
            </div>
            <MembersList members={members} />
          </div>
        )}
      </div>
      <BulkDuplicateModal
        key={`${duplicateModal?.type ?? 'closed'}-${id}`}
        count={duplicateModal?.count ?? 0}
        defaultProjectId={id}
        isPending={pendingAction === 'pathways-duplicate' || pendingAction === 'systems-duplicate'}
        isProjectsLoading={isProjectsLoading}
        initialNextPage={projectsNextPage}
        onClose={closeDuplicateModal}
        onSubmit={handleBulkDuplicateSubmit}
        open={!!duplicateModal}
        projects={projects}
        resourceLabel={duplicateModal?.type === 'system' ? 'systems' : 'pathways'}
      />
      <Outlet />
    </div>
  );
};

export default ProjectHomeRoute;
